import API from "./api";
import { queryClient } from "../lib/queryClient";
import { normalizeRole } from "../rbac/access";
import { orgScope } from "./orgScope";

const CACHE_KEY = "brixigo.dashboard.overview";
const CACHE_TTL_MS = 120_000;

function overviewCacheKey() {
  return `${CACHE_KEY}:${orgScope()}`;
}

export function dashboardOverviewQueryKey(role, locationId) {
  const normalized = normalizeRole(role) || "session";
  const location = locationId ? String(locationId) : "all";
  return ["dashboard-overview", orgScope(), normalized, location];
}

export function clearDashboardCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(`${CACHE_KEY}:`)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/** Clear session cache + React Query so Dashboard KPIs match live employee data. */
export function invalidateDashboardOverview(queryClient) {
  clearDashboardCache();
  queryClient?.invalidateQueries?.({ queryKey: ["dashboard-overview"] });
}

export function readDashboardCache() {
  try {
    if (orgScope() === "anonymous") return undefined;
    const key = overviewCacheKey();
    const raw = sessionStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (parsed?.org && parsed.org !== orgScope()) return undefined;
    if (!parsed?.data || Date.now() - (parsed.at || 0) > CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return undefined;
    }
    return parsed.data;
  } catch {
    return undefined;
  }
}

function writeDashboardCache(data) {
  try {
    const org = orgScope();
    if (org === "anonymous") return;
    sessionStorage.setItem(
      overviewCacheKey(),
      JSON.stringify({ at: Date.now(), org, data })
    );
  } catch {
    /* ignore quota errors */
  }
}

/** GET /api/dashboard/locations — active work locations for the dashboard filter */
export async function getDashboardLocations() {
  const { data } = await API.get("/dashboard/locations");
  return Array.isArray(data) ? data : [];
}

/**
 * GET /api/dashboard/overview — org-scoped payroll dashboard metrics.
 * Pass a work-location id to scope every metric to that location.
 * Query-client callers may pass a query context object; that is ignored.
 */
export async function getDashboardOverview(locationId) {
  const id = typeof locationId === "string" && locationId ? locationId : undefined;
  const { data } = await API.get("/dashboard/overview", {
    params: id ? { location_id: id } : undefined,
  });
  if (!id) writeDashboardCache(data);
  return data;
}

/**
 * GET /api/dashboard/activity — full Recent Activity history (newest first).
 * The dashboard card shows only the newest 5; this pages through everything.
 */
export async function getDashboardActivity({ locationId, limit = 50, offset = 0 } = {}) {
  const params = { limit, offset };
  if (locationId) params.location_id = locationId;
  const { data } = await API.get("/dashboard/activity", { params });
  return data;
}

export function prefetchDashboardOverview(client, role) {
  const qc = client || queryClient;
  if (!qc || !role) return undefined;
  return qc.prefetchQuery({
    queryKey: dashboardOverviewQueryKey(role, "all"),
    queryFn: () => getDashboardOverview(),
    staleTime: 30_000,
  });
}

/** Shared with the dashboard page, navbar, and payroll home. */
export function fetchCachedDashboardOverview(role, locationId) {
  return queryClient.fetchQuery({
    queryKey: dashboardOverviewQueryKey(role, locationId),
    queryFn: () => getDashboardOverview(locationId),
    staleTime: 30_000,
  });
}
